<template>
  <div
    :style="{
        width: '100%',
        backgroundColor: currentPageConfig?.info?.backgroundColor,
        backgroundImage: currentPageConfig?.info?.backgroundImage
          ? `url(${currentPageConfig.info.backgroundImage})`
          : ''
      }"
    class="page-wrap"
  >
    <div v-if="currentPageConfig">
     
      <div v-for="(item, idx) in currentPageConfig.elements" :key="idx">
        <element-item
          :disabled="!edit"
          :item-data="item"
          :idx="idx"
          :allArcListMap="allArcListMap"
          :parent-keys="[...parentKeys, idx]"
          :list-length="currentPageConfig.elements.length"
          :current-element-index="currentElementIndex"
          :current="idx + '' === currentElementIndexStr"
          :pages="pages"
        ></element-item>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, watch, inject, provide } from "vue"; 
import { get } from "lodash-es"; 
import { getActiveDetail } from "@/api";
import { makeAllArcListMap } from "@/utils/asyncData";

// Props
const props = defineProps({
  allArcListMap: {
    type: Object,
    default: () => ({})
  },
  currentElementIndex: {
    type: Array,
    default: () => []
  },
  idx: {
    type: Number,
    default: -1
  },
  parentKeys: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: true
  },
  config: {
    type: Object
  }
});

// Reactive State
const edit = ref(false);
const remotePages = ref(null);
const remoteAllArcListMap = ref({});
const { $customFetch } = useNuxtApp();
let requestToken = 0;

// Computed Properties
const currentElementIndexStr = computed(() => {
  return props.currentElementIndex && props.currentElementIndex.length
    ? props.currentElementIndex.join("x")
    : "";
});

const localPages = computed(() => {
  let res = [];
  try {
    res = JSON.parse(props.config.configData);
  } catch (e) {
    console.error("解析内容失败", e);
  }
  return res;
});

const pages = computed(() => {
  return remotePages.value || localPages.value;
});

const allArcListMap = computed(() => {
  return remotePages.value !== null
    ? remoteAllArcListMap.value
    : props.allArcListMap;
});

const currentPageConfig = computed(() => {
  return pages.value && pages.value[0] ? pages.value[0] : null;
});

const parentGetConfigJson = inject("getConfigJson", () => ({}));

function getComponentConfigJsonHook() {
  const parentConfig =
    typeof parentGetConfigJson === "function" ? parentGetConfigJson() : {};

  return {
    langJson: {
      ...(parentConfig?.langJson || {}),
      ...(currentPageConfig.value?.langJson || {}),
    },
    bindData: {
      ...(parentConfig?.bindData || {}),
      ...(currentPageConfig.value?.bindData || {}),
    },
  };
}

provide("getConfigJson", getComponentConfigJsonHook);

function setPageAppend() {
  if (currentPageConfig.value) {
    if (get(currentPageConfig.value, "info.cssText", "")) {
      useHead({
        style: [
          {
            body: true,
            innerHTML: get(currentPageConfig.value, "info.cssText", "")
          }
        ]
      });
    }
    if (
      process.client &&
      get(currentPageConfig.value, "info.javascriptText", "")
    ) {
      useHead({
        script: [
          {
            type: "text/javascript",

            body: true,
            innerHTML: get(currentPageConfig.value, "info.javascriptText", "")
          }
        ]
      });
    }
  }
}

function withParentKeysMap(map = {}) {
  const nextMap = {};

  Object.keys(map).forEach((key) => {
    const keyParts = String(key || "")
      .split(",")
      .filter((item) => item !== "");
    if (!keyParts.length) {
      return;
    }

    const nextKey = [...props.parentKeys, keyParts[0], ...keyParts].join(",");
    nextMap[nextKey] = map[key];
  });

  return nextMap;
}

async function fetchRemoteConfigById() {
  const configId = props.config?.id;

  if (!process.client || !configId) {
    remotePages.value = null;
    remoteAllArcListMap.value = {};
    return;
  }

  const currentToken = ++requestToken;

  try {
    const resAll = await getActiveDetail({
      id: configId,
      pageNo: 1,
      pageSize: 1,
      getCache: 0,
    });

    if (currentToken !== requestToken) {
      return;
    }

    const findData = get(resAll, "data.dataList[0]", null);
    const areaData = get(findData, "areaData", "");

    if (!areaData) {
      remotePages.value = null;
      remoteAllArcListMap.value = {};
      return;
    }

    let parsedPages = [];
    try {
      parsedPages = JSON.parse(areaData);
    } catch (error) {
      console.error("解析公用模板内容失败", error);
      remotePages.value = null;
      remoteAllArcListMap.value = {};
      return;
    }

    const resMakeData = await makeAllArcListMap(
      $customFetch,
      {},
      {
        dataList: [
          {
            areaData,
          },
        ],
      },
      {},
      parsedPages
    );

    if (currentToken !== requestToken) {
      return;
    }

    remotePages.value = resMakeData.pages || parsedPages || [];
    remoteAllArcListMap.value = withParentKeysMap(
      resMakeData.allArcListMap || {}
    );
  } catch (error) {
    if (currentToken !== requestToken) {
      return;
    }
    console.error("获取公用模板最新配置失败", error);
    remotePages.value = null;
    remoteAllArcListMap.value = {};
  }
}

watch(
  currentPageConfig,
  () => {
    setPageAppend();
  },
  { immediate: true }
);

watch(
  () => props.config?.id,
  () => {
    fetchRemoteConfigById();
  },
  { immediate: true }
);
</script>
